// FeedForm.jsx
import React, { useState, useContext, useEffect, useRef } from "react";
import { ImageIcon, X, UploadCloud, Loader2, Send, ImagePlus } from "lucide-react";
import { api } from "../api/axios";
import { toast } from "react-toastify";
import { AuthContext } from "../context/AuthContext";

/**
 * Native image compression helper:
 * - Resizes if width > maxWidth
 * - Controls JPEG/WebP quality
 * - Returns a File instance
 */
const compressImage = async (file, { maxWidth = 1200, quality = 0.7 } = {}) => {
    // If not an image, return original
    if (!file.type.startsWith("image/")) return file;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = (err) => reject(err);
        reader.onload = () => {
            const img = new Image();
            img.onerror = (err) => reject(err);
            img.onload = () => {
                let { width, height } = img;
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);

                // choose mime type fallback to original
                const mime = file.type || "image/jpeg";
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("Compression failed"));
                            return;
                        }
                        // keep original file name
                        const compressedFile = new File([blob], file.name, {
                            type: mime,
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    },
                    mime,
                    quality
                );
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });
};

export default function FeedForm() {
    const { setLoading, user } = useContext(AuthContext);
    const [localLoading, setLocalLoading] = useState(false);

    const [description, setDescription] = useState("");
    const [images, setImages] = useState([]); // compressed File objects
    const [previewImages, setPreviewImages] = useState([]); // { url, name }
    const [isDragging, setIsDragging] = useState(false);

    const fileRef = useRef(null);

    const MAX_IMAGES = 5;
    const MAX_CHARS = 350;
    const COMPRESSION_OPTIONS = { maxWidth: 1200, quality: 0.7 };

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            previewImages.forEach((p) => URL.revokeObjectURL(p.url));
        };
    }, [previewImages]);

    // Process incoming FileList (drag/drop or input)
    const handleFiles = async (fileList) => {
        const rawFiles = Array.from(fileList);
        if (rawFiles.length === 0) return;

        // validate count
        if (images.length + rawFiles.length > MAX_IMAGES) {
            toast.error(`You can upload a maximum of ${MAX_IMAGES} images.`);
            return;
        }

        // validate types
        const nonImages = rawFiles.filter((f) => !f.type.startsWith("image/"));
        if (nonImages.length > 0) {
            toast.error("Only image files are allowed.");
            return;
        }

        setLocalLoading(true);

        try {
            // compress in parallel (but don't overload memory)
            const compressPromises = rawFiles.map((f) => compressImage(f, COMPRESSION_OPTIONS));
            const compressed = await Promise.all(compressPromises);

            // create previews
            const newPreviews = compressed.map((f) => ({ url: URL.createObjectURL(f), name: f.name }));

            setImages((prev) => [...prev, ...compressed]);
            setPreviewImages((prev) => [...prev, ...newPreviews]);
        } catch (err) {
            console.error("Image processing error:", err);
            toast.error("Failed to process one or more images.");
        } finally {
            setLocalLoading(false);
        }
    };

    // input change handler
    const onInputChange = (e) => {
        if (!e.target.files) return;
        handleFiles(e.target.files);
        // reset input so same file can be re-selected later
        e.target.value = "";
    };

    // drag handlers
    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
    };

    // remove image at index
    const removeImage = (idx) => {
        if (!previewImages[idx]) return;
        URL.revokeObjectURL(previewImages[idx].url);
        setPreviewImages((prev) => prev.filter((_, i) => i !== idx));
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };

    // submit handler - uses formData.append('image', file) per file (Option 1)
    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = description.trim();

        if (images.length === 0) {
            toast.error("Please upload at least one image.");
            return;
        }
        if (!trimmed) {
            toast.error("Please enter a description.");
            return;
        }

        setLoading?.(true);
        setLocalLoading(true);

        try {
            const formData = new FormData();
            formData.append("description", trimmed);
            formData.append("userType", "admins");
            // keep your existing pattern for user id
            if (user?.id) formData.append("user", user.id);

            // Option 1: append multiple files under same key "image"
            images.forEach((file) => formData.append("image", file));

            const res = await api.post("/posts", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res.status === 201 || res.status === 200) {
                toast.success("Post Created Successfully");
                // reset
                setDescription("");
                // revoke urls
                previewImages.forEach((p) => URL.revokeObjectURL(p.url));
                setPreviewImages([]);
                setImages([]);
            } else {
                toast.error("Failed to create post.");
            }
        } catch (err) {
            console.error("Submit error:", err);
            toast.error("Post Submit Error");
        } finally {
            setLoading?.(false);
            setLocalLoading(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 font-sans">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <ImagePlus className="w-5 h-5 text-blue-600" />
                    Create New Post
                </h2>
                <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {images.length}/{MAX_IMAGES} Images
                </span>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* LEFT - inputs */}
                    <div className="space-y-6">
                        {/* Upload area */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Media Upload</label>

                            <div
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                onClick={() => fileRef.current?.click()}
                                className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-200
                  ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"}`}
                            >
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={onInputChange}
                                />

                                <div className="bg-white p-3 rounded-full shadow-sm mb-3">
                                    {localLoading ? (
                                        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                                    ) : (
                                        <UploadCloud className="w-6 h-6 text-blue-600" />
                                    )}
                                </div>

                                <p className="text-sm font-medium text-gray-700">
                                    {isDragging ? "Drop images here" : "Click to upload or drag & drop"}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP — max {MAX_IMAGES} images</p>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Description</label>
                            <div className="relative">
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    maxLength={MAX_CHARS}
                                    rows={6}
                                    placeholder="Write a caption..."
                                    className="w-full p-4 text-sm text-gray-800 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none placeholder:text-gray-400"
                                />
                                <div className="absolute bottom-3 right-3 text-xs font-medium text-gray-400 bg-white/80 px-2 py-1 rounded-md">
                                    {description.length} / {MAX_CHARS}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT - Live social preview */}
                    <div className="flex flex-col h-full">
                        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                            <ImageIcon className="w-4 h-4" /> Preview
                        </h3>

                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-fit select-none">
                            {/* header */}
                            <div className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                                        {user?.name?.[0]?.toUpperCase() || "U"}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 leading-tight">{user?.name || "Admin"}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">Just now</p>
                                    </div>
                                </div>
                                <div className="text-gray-300">•••</div>
                            </div>

                            {/* caption */}
                            <div className="px-4 pb-3">
                                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                    {description || <span className="text-gray-400 italic">Your caption will appear here...</span>}
                                </p>
                            </div>

                            {/* images area */}
                            <div className="w-full bg-gray-100 min-h-[220px] border-y border-gray-100 relative">
                                {previewImages.length === 0 ? (
                                    <div className="h-48 flex flex-col items-center justify-center text-gray-400">
                                        <ImageIcon className="w-12 h-12 opacity-20 mb-2" />
                                        <span className="text-sm opacity-50">Image preview area</span>
                                    </div>
                                ) : (
                                    <div className={`grid gap-0.5 ${previewImages.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                                        {previewImages.map((p, idx) => (
                                            <div
                                                key={idx}
                                                className={`relative bg-gray-200 overflow-hidden group ${previewImages.length === 1 ? "aspect-video" : "aspect-square"}`}
                                            >
                                                <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-start justify-end p-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeImage(idx)}
                                                        className="p-1.5 bg-black/60 text-white rounded-full hover:bg-red-500 opacity-0 group-hover:opacity-100 transition-all transform scale-90 group-hover:scale-100 shadow-lg"
                                                        title="Remove this image"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* fake actions footer */}
                            <div className="p-3 flex items-center justify-between text-gray-500">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1.5 cursor-default">
                                        <span className="text-xs font-medium">0</span>
                                        <span className="text-xs text-gray-400">Likes</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 cursor-default">
                                        <span className="text-xs font-medium">0</span>
                                        <span className="text-xs text-gray-400">Comments</span>
                                    </div>
                                </div>
                                <div className="text-xs text-gray-400">Share</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* footer buttons */}
                <div className="mt-8 flex justify-end items-center gap-4 pt-6 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={() => {
                            previewImages.forEach((p) => URL.revokeObjectURL(p.url));
                            setPreviewImages([]);
                            setImages([]);
                            setDescription("");
                        }}
                        className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={localLoading}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={localLoading || images.length === 0}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white shadow-lg transition-all transform active:scale-95
              ${localLoading || images.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"}
            `}
                    >
                        {localLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                Publish Post
                                <Send className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
